import React, {
	type Context,
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { Popup } from '@atlaskit/popup/popup';
import type { ContentProps, PopupProps, TriggerProps } from '@atlaskit/popup/types';
import Spinner from '@atlaskit/spinner/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types/types';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container/ui';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useHoverTriggerRef } from './useHoverTriggerRef';
import { usePreloadRef } from './usePreloadRef';
import { usePressableTriggerRef } from './usePressableTriggerRef';

export type TriggerMode = 'hover' | 'click' | Array<'hover' | 'click'>;

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointPropsType<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

const FallbackComponent = ({
	fallback,
	onUnmount,
}: {
	fallback?: ReactNode;
	onUnmount: () => void;
}) => {
	useEffect(() => {
		return () => onUnmount();
	}, [onUnmount]);

	return <>{fallback || <Spinner />}</>;
};

export type PopupTriggerWithHoverProps<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointPropsType<TEntryPoint>, 'onClose' | 'onContentResized'> & {
		onClose?: () => void;
		onContentResized?: ContentProps['update'];
	};
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	forcedReportErrorUFO?: boolean;
	onError?: ErrorCallback;
	/** Called after the popup opens. `preloaded` is true when the open reused a hover preload. */
	onOpen?: (method: 'hover' | 'click', details: { preloaded: boolean }) => void;
	/** Called when a hover preload request is dispatched, before the popup opens. */
	onPreload?: () => void;
	trigger: (triggerProps: TriggerProps) => ReactNode;
	/**
	 * How the popup should be triggered. Defaults to `'click'` when omitted.
	 */
	triggerMode?: TriggerMode;
	/**
	 * Overrides the hover open/close delays (ms) used when `triggerMode` includes
	 * `'hover'`. Defaults to the adaptor's built-in delays when omitted.
	 */
	delays?: {
		hoverOpen?: number;
		hoverClose?: number;
		/**
		 * How long (ms) the pointer must rest on the trigger before the entry point is preloaded.
		 * Raise it when the preloaded query is expensive enough that speculative hovers are not
		 * worth serving. Defaults to the adaptor's built-in delay when omitted.
		 */
		preloadIntent?: number;
	};
	/**
	 * Keep an entry point preload that has already fired when the pointer leaves the trigger, so
	 * hovering again or opening reuses it instead of loading again. Defaults to false.
	 */
	keepPreloadOnLeave?: boolean;
	/** Return `true` to veto any close (outside click, Escape, blur, hover-out). */
	shouldPreventClose?: () => boolean;
} & CustomPopupProps;

/** Lets popup content nominate the element that receives focus when the popup opens. */
export const PopupInitialFocusContext: Context<(element: HTMLElement | null) => void> =
	createContext<(element: HTMLElement | null) => void>(() => {});

/** Returns a ref callback that nominates its element as the popup's initial focus target. */
export function usePopupInitialFocusRef(): (element: HTMLElement | null) => void {
	return useContext(PopupInitialFocusContext);
}

export function PopupTriggerWithHover<TEntryPoint extends AnyEntryPoint>({
	trigger,
	triggerMode = 'click',
	delays,
	keepPreloadOnLeave,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	forcedReportErrorUFO,
	onOpen,
	onPreload,
	onClose,
	shouldPreventClose,
	...popupProps
}: PopupTriggerWithHoverProps<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<unknown>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const updateRef = useRef<(() => void) | null>(null);
	const openedByRef = useRef<'hover' | 'click' | null>(null);
	const isOpenRef = useRef(isOpen);
	const triggers = Array.isArray(triggerMode) ? triggerMode : [triggerMode];

	useLayoutEffect(() => {
		isOpenRef.current = isOpen;
	}, [isOpen]);

	const handleOpen = useCallback(
		(method: 'hover' | 'click' | null, preloaded: boolean) => {
			setIsOpen(true);
			if (method) {
				onOpen?.(method, { preloaded });
			}
		},
		[onOpen],
	);

	const handleClose = useCallback(
		(
			event?: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			// Every close path funnels through here, so this one check covers them all.
			if (shouldPreventClose?.()) {
				return;
			}
			setIsOpen(false);
			onClose?.(event ?? null, currentLevel);
		},
		[onClose, shouldPreventClose],
	);

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({
			reference: entryPointReference,
			preloaded,
		}: {
			reference: EntryPointRef<TEntryPoint>;
			preloaded: boolean;
		}) => {
			if (isOpenRef.current) {
				return;
			}

			handleOpen(openedByRef.current, preloaded);

			setContent(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={
						<FallbackComponent fallback={fallback} onUnmount={() => updateRef.current?.()} />
					}
					errorFallback={errorFallback}
					runtimeProps={
						{
							...(entryPointProps ?? emptyEntryPointProps),
							onContentResized: () => {
								updateRef.current?.();
							},
							onClose: handleClose,
						} as unknown as EntryPointPropsType<TEntryPoint>
					}
					onError={onError}
					forcedError={forcedReportErrorUFO}
				/>,
			);
		},
		[
			setContent,
			id,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			handleOpen,
			onError,
			handleClose,
		],
	);

	const preloadRef = usePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		onPreload,
		timeToIntent: delays?.preloadIntent,
		keepPreloadOnLeave,
	});

	const pressableRef = usePressableTriggerRef({
		onOpen: preloadRef.loadAndOpen,
		onClose: handleClose,
		isOpen,
		isDisabled: !triggers.includes('click'),
		openedByRef,
	});

	const { triggerRef: hoverTriggerRef, contentRef: hoverContentRef } = useHoverTriggerRef({
		onOpen: preloadRef.loadAndOpen,
		onClose: handleClose,
		isOpen,
		isDisabled: !triggers.includes('hover'),
		openedByRef,
		delays,
	});

	useEffect(() => {
		if (!isOpen) {
			openedByRef.current = null;
		}
	}, [isOpen]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([preloadRef, pressableRef, hoverTriggerRef, triggerProps.ref]);
			return trigger({ ...triggerProps, ref: mergedRef });
		},
		[trigger, preloadRef, pressableRef, hoverTriggerRef],
	);

	const popupContent = useCallback(
		({ update, setInitialFocusRef }: ContentProps) => {
			updateRef.current = update;
			return (
				<div ref={hoverContentRef} role="none">
					<PopupInitialFocusContext.Provider value={setInitialFocusRef}>
						{content}
					</PopupInitialFocusContext.Provider>
				</div>
			);
		},
		[content, hoverContentRef],
	);

	return (
		<Popup
			{...popupProps}
			isOpen={isOpen}
			onClose={handleClose}
			trigger={popupTrigger}
			content={popupContent}
		/>
	);
}
